/**
 * Der Client zu `/api/risikoanalyse-uploads` — Beratermodus (Tor A).
 *
 * Der Controller (`apps/api/src/risikoanalyse-upload.controller.ts`) hat drei
 * Autorisierungstore; dieser Client spricht **nur** das erste an: die
 * `contact/:contactId`-Routen des eingeloggten Beraters. Der öffentliche
 * Token-Pfad (`token/:token`, Plan AP7) und das Portal (AP10) sind bewusst
 * nicht hier — sie brauchen keinen Bearer und einen eigenen Fehlerdialog.
 *
 * **Kein `Content-Type` von Hand.** Bei `FormData` setzt ihn der Browser samt
 * `boundary`; ein selbst gesetzter Header zerlegte den Multipart-Body
 * (`apps/web/src/utils/api.ts` hängt nur den Bearer an, siehe dort).
 *
 * **Fachliche Fehlermeldungen kommen vom Server, nicht von hier.** Grenzen und
 * Wortlaut stehen in `apps/api/src/risikoanalyse-upload.constants.ts` bzw. im
 * Service daneben („Für ‚Bilanz‘ sind maximal 5 Dateien möglich…", „Das
 * Speicherkontingent für diese Analyse ist erschöpft…", „Dieser Dateityp wird
 * nicht akzeptiert…") — alle auf Deutsch und alle für den Nutzer gedacht.
 * Dieser Client reicht sie durch, statt sie ein zweites Mal zu formulieren.
 *
 * **Was NICHT vom Fachcode kommt, bekommt hier einen eigenen Satz.** Vier
 * Antworten entstehen vor bzw. neben dem Handler und tragen englischen
 * Infrastruktur-Text, den das Feld sonst wörtlich als `role="alert"` anzeigte:
 *
 *  - `413` von multer: hart „File too large" (englisch, ohne Zahl).
 *  - `401` vom AuthzGuard: „Missing or invalid session" — nach Sitzungsablauf.
 *  - `403` vom AuthzGuard: „Missing required scope" — der Fall tritt real ein,
 *    weil die Route des Fragebogens nur `contacts:read` verlangt, der Upload
 *    aber `contacts:update`, und `deriveMatrixReadPermissions`
 *    (`rbac.service.ts`) leitet aus der Matrix ausschließlich `:read` ab.
 *  - `429` vom Rate-Limiter: der Default von express-rate-limit geht als
 *    text/html raus, es gibt also gar keinen JSON-Body zum Durchreichen.
 *
 * **Fortschrittsbalken bewusst nicht.** Der Plan (§7.2) sieht XHR mit
 * `upload.onprogress` vor, weil `fetch` keinen Upload-Fortschritt kennt. XHR
 * läuft aber am gepatchten `window.fetch` vorbei und müsste den Bearer selbst
 * aus dem localStorage holen — ein zweiter Auth-Pfad im Frontend, wo es heute
 * genau einen gibt. Für den Beratermodus (10 MB je Datei, Büro-Netz) wiegt das
 * schwerer als der Balken; die Kachel zeigt stattdessen „wird hochgeladen…".
 * Für den Token-Pfad (AP7) fällt der Einwand weg — dort ist gar kein Bearer im
 * Spiel und XHR kostet nichts.
 */
import { apiUrl } from "../utils/api";
import { collectFields, documentRefs } from "@kmvs/forms-core";
import type { Answers, DocumentRef, FormSchema } from "@kmvs/forms-core";

/** Slot-Definition, wie der Server sie kennt (`listSlots` im Upload-Service). */
export type AnalyseUploadSlot = {
  fieldKey: string;
  label: string;
  /** Ordnersegment unter „Risikoanalyse/" im Dokumente-Explorer. */
  ordner: string;
  maxFiles: number;
  /** true = Slot gehört ausschließlich in den Beratermodus. */
  nurBerater?: boolean;
  /** Erlaubte MIME-Types laut Server. */
  accept?: string[];
};

/** Antwort von `GET risikoanalyse-uploads/contact/:contactId`. */
export type AnalyseUploadListe = {
  slots: AnalyseUploadSlot[];
  /** Aktive Dateien, gruppiert nach forms-core-Feld-Key. */
  dateien: Record<string, DocumentRef[]>;
};

/**
 * Ein Fehler, den das Feld dem Nutzer zeigen darf.
 *
 * `message` ist immer ein für Menschen lesbarer deutscher Satz — entweder der
 * des Servers oder (nur beim 413 und bei Verbindungsabbruch) der hier
 * hinterlegte. `status` bleibt erhalten, damit der Aufrufer unterscheiden
 * kann, ohne den Text zu zerlegen; `0` heißt „Anfrage kam nie an".
 */
export class AnalyseUploadFehler extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "AnalyseUploadFehler";
    this.status = status;
  }
}

/**
 * Multer bricht den Stream bei Überschreitung ab; NestJS macht daraus einen
 * 413 mit multers englischer Meldung „File too large". Die Zahl dazu kennt der
 * Client aus dem FieldDef (`maxSizeMb`) und prüft deshalb schon vorher — dieser
 * Satz greift nur, wenn der Slot keine Grenze mitbringt oder eine größere als
 * der Server.
 */
const MELDUNG_ZU_GROSS = "Die Datei ist zu groß für den Upload.";

/** Kein HTTP-Status: DNS, Offline, abgebrochene Verbindung. */
const MELDUNG_KEINE_VERBINDUNG =
  "Der Upload ist fehlgeschlagen — bitte Verbindung prüfen und erneut versuchen.";

/** Server erreicht, aber ohne verwertbaren Text (z. B. 502 vom Proxy). */
const MELDUNG_UNBEKANNT = "Das hat nicht geklappt. Bitte erneut versuchen.";

/** 401 — die Sitzung ist abgelaufen; der Guard antwortet englisch. */
const MELDUNG_ABGEMELDET =
  "Die Sitzung ist abgelaufen. Bitte neu anmelden und den Upload wiederholen.";

/**
 * 403 — bewusst neutral formuliert: dieselbe Antwort kommt vom Rechte-Guard
 * (`contacts:update` fehlt) UND von der Zeilenprüfung des Kontakts, und sie
 * trifft sowohl den Upload als auch das reine Abrufen der Dateiliste.
 */
const MELDUNG_KEINE_RECHTE = "Für diesen Kontakt fehlen die nötigen Rechte.";

/** 429 — der eigene Rate-Limit-Eimer des Upload-Pfades (`main.ts`). */
const MELDUNG_ZU_VIELE =
  "Zu viele Uploads in kurzer Zeit — bitte eine Minute warten und dann fortfahren.";

/**
 * Holt den Meldungstext aus einer Fehlerantwort.
 *
 * NestJS antwortet `{ statusCode, message, error }`; `message` ist bei
 * Validierungsfehlern ein Array. Ist der Body kein JSON (nginx-Fehlerseite),
 * bleibt der Text leer und der Aufrufer nimmt seinen Rückfall.
 */
const serverMeldung = async (res: Response): Promise<string> => {
  try {
    const body = await res.json();
    const roh = (body as { message?: unknown })?.message;
    if (typeof roh === "string") return roh.trim();
    if (Array.isArray(roh)) return roh.filter((t) => typeof t === "string").join(" ").trim();
  } catch {
    // Kein JSON — bewusst still, der Aufrufer hat einen Rückfall.
  }
  return "";
};

/**
 * Wandelt eine Fehlerantwort in den Fehler, den das Feld anzeigen darf.
 *
 * Die vier Sonderfälle stehen VOR dem Durchreichen: ihre Bodies stammen nicht
 * aus dem Analyse-Code (multer, AuthzGuard, express-rate-limit) und sind
 * englisch bzw. gar kein JSON — durchgereicht landeten sie wörtlich in der
 * Fehlerzeile des Slots. Alles andere bleibt Server-Wortlaut.
 */
const fehlerAusAntwort = async (res: Response): Promise<AnalyseUploadFehler> => {
  if (res.status === 413) return new AnalyseUploadFehler(MELDUNG_ZU_GROSS, 413);
  if (res.status === 401) return new AnalyseUploadFehler(MELDUNG_ABGEMELDET, 401);
  if (res.status === 403) return new AnalyseUploadFehler(MELDUNG_KEINE_RECHTE, 403);
  if (res.status === 429) return new AnalyseUploadFehler(MELDUNG_ZU_VIELE, 429);
  const text = await serverMeldung(res);
  return new AnalyseUploadFehler(text || MELDUNG_UNBEKANNT, res.status);
};

/** Netzwerkfehler von `fetch` — nie ein HTTP-Status, deshalb `0`. */
const alsVerbindungsfehler = (err: unknown): AnalyseUploadFehler =>
  err instanceof AnalyseUploadFehler
    ? err
    : new AnalyseUploadFehler(MELDUNG_KEINE_VERBINDUNG, 0);

/**
 * Die vom Server geführte Dateiliste eines Kontakts.
 *
 * Das ist die Wahrheit über hochgeladene Unterlagen — nicht der Antwortsatz
 * im Entwurf. Wer beides hat, überschreibt den Entwurf hiermit
 * ({@link mitServerDokumenten}).
 */
export const holeAnalyseUploads = async (
  contactId: number,
  signal?: AbortSignal,
): Promise<AnalyseUploadListe> => {
  let res: Response;
  try {
    res = await fetch(apiUrl(`risikoanalyse-uploads/contact/${contactId}`), { signal });
  } catch (err) {
    throw alsVerbindungsfehler(err);
  }
  if (!res.ok) throw await fehlerAusAntwort(res);
  const body = (await res.json()) as Partial<AnalyseUploadListe>;
  return {
    slots: Array.isArray(body?.slots) ? body.slots : [],
    dateien:
      body?.dateien && typeof body.dateien === "object" && !Array.isArray(body.dateien)
        ? (body.dateien as Record<string, DocumentRef[]>)
        : {},
  };
};

/**
 * Wie lange eine Oberfläche auf die Unterlagen-Liste wartet.
 *
 * Ohne Frist bliebe bei einer hängenden Antwort dauerhaft „Formular wird
 * geladen…" stehen — eine Analyse, die heute ausfüllbar ist, wäre es wegen
 * einer Dateiliste nicht mehr. Nach der Frist arbeitet die Oberfläche mit dem,
 * was sie hat, und sagt das auch (Hinweis am Formular, Abbruch beim
 * Bestätigen).
 */
export const UPLOAD_LISTE_FRIST_MS = 8000;

/**
 * {@link holeAnalyseUploads} mit Frist — der Weg, den jede Oberfläche nimmt.
 *
 * Bewusst hier und nicht je Aufrufer: Drei Stellen lesen die Liste
 * (Fragebogen, Auswertungsseite, Prüf-Modal), und eine davon ohne Frist ist
 * genau die, die irgendwann hängt.
 */
export const holeAnalyseUploadsMitFrist = async (
  contactId: number,
  fristMs: number = UPLOAD_LISTE_FRIST_MS,
): Promise<AnalyseUploadListe> => {
  const abbruch = new AbortController();
  const frist = setTimeout(() => abbruch.abort(), fristMs);
  try {
    return await holeAnalyseUploads(contactId, abbruch.signal);
  } finally {
    clearTimeout(frist);
  }
};

/**
 * Lädt EINE Datei in einen Slot und liefert die Referenz, die der Server
 * erzeugt hat.
 *
 * `fieldKey` steht doppelt im Request — im Body **und** in der Query. Der
 * Controller liest beides (Kopfkommentar dort): multer füllt `req.body` nur
 * mit Textteilen, die im Stream VOR der Datei stehen; die Query kommt
 * reihenfolgeunabhängig an. Die Datei wird trotzdem zuletzt angehängt, damit
 * beide Wege tragen.
 */
export const ladeAnalyseUploadHoch = async (args: {
  contactId: number;
  fieldKey: string;
  datei: File;
  note?: string;
  signal?: AbortSignal;
}): Promise<DocumentRef> => {
  const formData = new FormData();
  formData.append("fieldKey", args.fieldKey);
  if (args.note) formData.append("note", args.note);
  formData.append("file", args.datei, args.datei.name);

  let res: Response;
  try {
    res = await fetch(
      apiUrl(`risikoanalyse-uploads/contact/${args.contactId}`, {
        fieldKey: args.fieldKey,
      }),
      { method: "POST", body: formData, signal: args.signal },
    );
  } catch (err) {
    throw alsVerbindungsfehler(err);
  }
  if (!res.ok) throw await fehlerAusAntwort(res);
  return (await res.json()) as DocumentRef;
};

/**
 * Hängt eine Datei aus dem Fragebogen ab.
 *
 * Kein Hard-Delete: der Server setzt nur `riskAnalysisUploadStatus =
 * 'verworfen'`, die Datei bleibt in der Kundenakte auffindbar.
 */
export const entferneAnalyseUpload = async (
  contactId: number,
  documentId: number,
): Promise<void> => {
  let res: Response;
  try {
    res = await fetch(
      apiUrl(`risikoanalyse-uploads/contact/${contactId}/${documentId}`),
      { method: "DELETE" },
    );
  } catch (err) {
    throw alsVerbindungsfehler(err);
  }
  if (!res.ok) throw await fehlerAusAntwort(res);
};

// ── Die Serverliste gewinnt gegen den Entwurf ─────────────────────────

/** Alle `documents`-Feld-Keys eines Schemas (inklusive Folgefelder). */
export const dokumentKeysDesSchemas = (schema: FormSchema): string[] =>
  collectFields(schema)
    .filter((field) => field.type === "documents")
    .map((field) => field.key);

/** Gleiche Dateien in gleicher Reihenfolge — und kein Fremdeintrag dazwischen. */
const listeIstGleich = (bisher: unknown, vomServer: readonly DocumentRef[]): boolean => {
  if (!Array.isArray(bisher)) return false;
  // `documentRefs` wirft Fremdeinträge weg; weicht die Länge davon ab, steht
  // im Entwurf etwas, das dort nicht hingehört — dann gewinnt der Server.
  const brauchbar = documentRefs(bisher);
  if (brauchbar.length !== bisher.length) return false;
  if (brauchbar.length !== vomServer.length) return false;
  return brauchbar.every((ref, i) => ref.documentId === vomServer[i]?.documentId);
};

/**
 * Legt die Serverliste über einen Antwortsatz — der Server gewinnt.
 *
 * **Warum das nötig ist.** Die Dateien liegen in der Datenbank, die
 * `DocumentRef`-Liste im Antwortsatz ist nur eine Kopie. Der Antwortsatz kommt
 * aber aus zwei Quellen, die beide veralten können: dem Entwurf auf dem
 * Kontakt (Server-jsonb) und dem localStorage-Entwurf des Browsers, der im
 * Ladepfad von `RisikoanalyseStepForm` sogar zuletzt gewinnt. Beide kennen nur
 * den Stand des Geräts, das zuletzt geschrieben hat:
 *
 *  - Ein Kollege lädt am zweiten Rechner eine Bilanz hoch ⇒ im eigenen
 *    localStorage-Entwurf fehlt sie, und der nächste Speichervorgang schriebe
 *    die Liste ohne sie zurück.
 *  - Eine Datei wurde entfernt (`verworfen`) ⇒ der alte Entwurf zeigte sie
 *    weiter an, mit einem Download-Ziel, das es nicht mehr gibt.
 *
 * Deshalb: **nach** dem Zusammenbau aller Entwurfsquellen und **vor** dem
 * ersten Autosave einmal die Serverliste darüberlegen. Danach hält jeder
 * Upload/Entfernen-Vorgang die Liste selbst aktuell, weil er die Antwort des
 * Servers übernimmt.
 *
 * Was bewusst NICHT passiert:
 *  - Keys, die weder der Server noch der Entwurf kennt, bleiben **abwesend**.
 *    Elf leere Arrays in jedem Antwortsatz wären reines Rauschen in `jsonb`.
 *  - Ein Fremdwert (Text aus der Zeit vor den Slots) unter einem leeren
 *    Server-Slot bleibt stehen. Ihn wegzuwerfen hieße, eine Angabe des Kunden
 *    zu löschen, die niemand ersetzt hat; `validateFormAnswers` meldet sie
 *    ohnehin als `invalidValues`.
 *  - Ändert sich nichts, kommt das URSPRÜNGLICHE Objekt zurück. Ein neues
 *    Objekt löste in `RisikoanalyseStepForm` einen Autosave aus, der nichts
 *    ändert.
 */
export const mitServerDokumenten = (
  answers: Answers,
  dokumentKeys: readonly string[],
  gruppen: Record<string, DocumentRef[]>,
): Answers => {
  let geaendert = false;
  const next: Answers = { ...answers };

  for (const key of dokumentKeys) {
    const vomServer = documentRefs(gruppen?.[key]);
    const bisher = next[key];

    if (vomServer.length === 0) {
      // Nichts auf dem Server: nur korrigieren, wenn der Entwurf Dateien
      // behauptet, die es nicht (mehr) gibt.
      if (documentRefs(bisher).length === 0) continue;
      next[key] = [];
      geaendert = true;
      continue;
    }

    if (listeIstGleich(bisher, vomServer)) continue;
    next[key] = vomServer;
    geaendert = true;
  }

  return geaendert ? next : answers;
};
